import { Patient } from '@dataTypes/PatientInfo.types'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import {
  formatClinicianDisplayName,
  isHygienistWorkRole,
} from '@utils/provider-display.utils'
import moment from 'moment'
import React, { useMemo } from 'react'
import { useAppSelector } from '@store/hooks'
import { useMembershipSidePanelOverview } from '@store/membershipSidePanelOverview.store'
import { ReactComponent as ContactInformationIcon } from '@assets/images/Sidepanel/contact-information.svg'
import { ReactComponent as MembershipOverviewIcon } from '@assets/images/Sidepanel/membership-overview.svg'
import { ReactComponent as VisitSummaryIcon } from '@assets/images/Sidepanel/visit-summary.svg'
import { ReactComponent as PhoneIcon } from '@assets/images/Sidepanel/phone.svg'
import { ReactComponent as FamilyEditIcon } from '@assets/images/Sidepanel/family-edit.svg'
import {
  formatTitleList,
  getPatientAllergyTitles,
  getPatientMedicalConditionTitles,
} from '@utils/patient-allergies-conditions.util'

type DetailsProps = {
  patient: Patient | null
  handleSetSchedule: () => void
  viewFamilyMembers: () => void
  onEdit?: () => void
  isHIPAAView?: boolean
  /** When false, Visit summary is omitted. Default true. */
  showVisitSummary?: boolean
  /** When false, Membership overview is omitted (Patient Profile Aside / Patient sidepanel). Default true. */
  showMembershipOverview?: boolean
  /** Rendered after Preferences (and Visit summary when shown). */
  afterPreferences?: React.ReactNode
}

const hasMeaningfulValue = (value?: string | null): boolean => {
  if (!value) return false
  const cleaned = value.trim().toLowerCase()
  if (!cleaned) return false
  const placeholders = ['none', 'n/a', 'na', 'nil', 'null', '-', 'no']
  return !placeholders.includes(cleaned)
}

const contactPreferenceLabels = (patient: Patient): string => {
  const bits: string[] = []
  if (patient.contact_via_sms) bits.push('SMS')
  if (patient.contact_via_phone) bits.push('Phonecall')
  if (patient.contact_via_email) bits.push('Email')
  if (patient.contact_via_app) bits.push('App')
  return bits.length ? bits.join(', ') : 'N/A'
}

const formatAddress = (patient: Patient | null): string => {
  if (!patient) return 'N/A'
  const line = [patient.address, patient.city, patient.state, patient.zip]
    .filter((part) => hasMeaningfulValue(part))
    .join(', ')
  return line || 'N/A'
}

const SectionHeader: React.FC<{
  iconClass?: string
  icon?: React.ReactNode
  title: string
  showEdit?: boolean
  onEdit?: () => void
}> = ({ iconClass, icon, title, showEdit, onEdit }) => (
  <div className="sp-details-section-header">
    <div className="sp-details-section-icon">
      {icon ?? (iconClass ? <i className={iconClass} aria-hidden /> : null)}
    </div>
    <span className="sp-details-section-title">{title}</span>
    {showEdit && onEdit && (
      <button
        type="button"
        className="sp-details-section-edit"
        aria-label={`Edit ${title}`}
        onClick={onEdit}
      >
        <FamilyEditIcon className="sp-details-section-edit-svg" aria-hidden />
      </button>
    )}
  </div>
)

const DetailField: React.FC<{
  label: string
  value: React.ReactNode
  className?: string
}> = ({ label, value, className }) => (
  <div className={`sp-details-field${className ? ` ${className}` : ''}`}>
    <span className="sp-details-field-label">{label}</span>
    <span className="sp-details-field-value">{value}</span>
  </div>
)

const Details: React.FC<DetailsProps> = ({
  patient,
  handleSetSchedule,
  viewFamilyMembers: _viewFamilyMembers,
  onEdit,
  isHIPAAView: _isHIPAAView = false,
  showVisitSummary = true,
  showMembershipOverview = true,
  afterPreferences,
}): React.ReactElement => {
  const providerList = useAppSelector((state) => state.provider.data)

  const phones = [
    { number: patient?.cellphone, label: 'Mobile' },
    { number: patient?.home_phone, label: 'Home' },
    { number: patient?.work_phone, label: 'Work' },
    { number: patient?.fax_number, label: 'Fax' },
  ].filter((entry) => hasMeaningfulValue(entry.number))

  const addressLine = formatAddress(patient)

  const medicalConditionsDisplay = formatTitleList(
    getPatientMedicalConditionTitles(patient),
  )
  const allergiesDisplay = formatTitleList(getPatientAllergyTitles(patient))

  const preferredProviderDisplay = useMemo(() => {
    const preferredId =
      (typeof patient?.preferred_provider === 'string' &&
      patient.preferred_provider
        ? patient.preferred_provider
        : null) ||
      patient?.preferredProvider?.provider_id ||
      null

    const fromStore = preferredId
      ? providerList.find((p) => p.provider_id === preferredId)
      : undefined

    const clinician =
      fromStore ||
      patient?.preferredProvider ||
      patient?.preferred_provider_info ||
      null

    if (clinician) {
      return formatClinicianDisplayName(clinician, {
        fallback: 'N/A',
        preferStyle: isHygienistWorkRole(clinician.work_role)
          ? 'hygienist'
          : 'provider',
      })
    }

    return hasMeaningfulValue(patient?.preferred_provider_name)
      ? (patient?.preferred_provider_name as string)
      : 'N/A'
  }, [patient, providerList])

  const preferredHygienistDisplay = useMemo(() => {
    const preferredId =
      (typeof patient?.preferred_hygienist === 'string' &&
      patient.preferred_hygienist
        ? patient.preferred_hygienist
        : null) ||
      patient?.preferredHygienist?.provider_id ||
      null

    const fromStore = preferredId
      ? providerList.find((p) => p.provider_id === preferredId)
      : undefined

    const clinician =
      fromStore ||
      patient?.preferredHygienist ||
      patient?.preferred_hygienist_info ||
      null

    if (clinician) {
      return formatClinicianDisplayName(clinician, {
        fallback: 'N/A',
        preferStyle: 'hygienist',
      })
    }

    return hasMeaningfulValue(patient?.preferred_hygienist_name)
      ? (patient?.preferred_hygienist_name as string)
      : 'N/A'
  }, [patient, providerList])

  const selectedMembershipOverview = useMembershipSidePanelOverview()
  const membershipTierDisplay = selectedMembershipOverview?.tier ?? 'N/A'
  const memberSinceDisplay = selectedMembershipOverview?.memberSince ?? 'N/A'
  const billingFrequencyDisplay =
    selectedMembershipOverview?.billingFrequency ?? 'N/A'
  const nextPaymentDisplay =
    selectedMembershipOverview?.nextPaymentDate ?? 'N/A'

  return (
    <div className="patient-details sp-details">
      {showVisitSummary ? (
        <section className="sp-details-section">
          <SectionHeader
            icon={
              <VisitSummaryIcon
                className="sp-details-section-svg-icon"
                aria-hidden
              />
            }
            title="Visit Summary"
          />
          <div className="sp-details-section-body sp-details-fields">
            <div className="sp-details-stat-row">
              <div className="sp-details-stat-card">
                <span className="sp-details-stat-label">
                  Appointment visits
                </span>
                <span className="sp-details-stat-value">
                  {patient?.no_of_visits ?? 'N/A'}
                </span>
              </div>
              <div className="sp-details-stat-card">
                <span className="sp-details-stat-label">First visit</span>
                <span className="sp-details-stat-value">
                  {patient?.date_first_visit
                    ? moment(patient.date_first_visit).format('MM-DD-YYYY')
                    : 'N/A'}
                </span>
              </div>
            </div>
            <div className="sp-details-stat-card sp-details-stat-card--full">
              <span className="sp-details-stat-label">Next visit</span>
              <div className="sp-details-next-visit">
                {patient?.date_next_visit ? (
                  <span className="sp-details-stat-value">
                    {moment(patient.date_next_visit).format('MM-DD-YYYY')}
                  </span>
                ) : (
                  <>
                    <span className="sp-details-field-value sp-details-field-value--muted">
                      N/A
                    </span>
                    <button
                      type="button"
                      className="unstyled-button sp-details-link sp-details-link--schedule"
                      onClick={handleSetSchedule}
                    >
                      Schedule
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <section className="sp-details-section">
        <SectionHeader iconClass="mgc_user_3_fill" title="Personal details" />
        <div className="sp-details-section-body sp-details-fields">
          <div className="sp-details-grid-row">
            <DetailField
              label="Medical conditions"
              value={medicalConditionsDisplay}
            />
            <DetailField label="Allergies" value={allergiesDisplay} />
          </div>
        </div>
      </section>

      {showMembershipOverview ? (
        <section className="sp-details-section">
          <SectionHeader
            icon={
              <MembershipOverviewIcon
                className="sp-details-section-svg-icon"
                aria-hidden
              />
            }
            title="Membership overview"
          />
          <div className="sp-details-section-body sp-details-fields">
            <div className="sp-details-grid-row">
              <DetailField
                label="Membership tier"
                value={membershipTierDisplay}
              />
              <DetailField label="Member since" value={memberSinceDisplay} />
            </div>
            <div className="sp-details-grid-row">
              <DetailField
                label="Billing frequency"
                value={billingFrequencyDisplay}
              />
              <DetailField
                label="Next payment date"
                value={nextPaymentDisplay}
              />
            </div>
          </div>
        </section>
      ) : null}

      <section className="sp-details-section">
        <SectionHeader
          icon={
            <ContactInformationIcon
              className="sp-details-section-svg-icon"
              aria-hidden
            />
          }
          title="Contact information"
          showEdit
          onEdit={onEdit}
        />
        <div className="sp-details-section-body">
          {phones.length > 0 && (
            <div className="sp-details-phone-block">
              <div className="sp-details-phone-numbers">
                <div className="sp-details-phone-icon" aria-hidden>
                  <PhoneIcon className="sp-details-section-svg-icon" />
                </div>
                <div className="sp-details-phone-list">
                  {phones.map((phone) => (
                    <span
                      className="sp-details-phone-number"
                      key={`${phone.label}-${phone.number}`}
                    >
                      {formatPhoneNumber(phone.number as string, false) ||
                        phone.number}
                    </span>
                  ))}
                </div>
              </div>
              <div className="sp-details-phone-tags">
                {phones.map((phone) => (
                  <span
                    className="sp-details-phone-tag"
                    key={`tag-${phone.label}-${phone.number}`}
                  >
                    {phone.label}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="sp-details-email-row">
            <div className="sp-details-phone-icon" aria-hidden>
              <i className="ti ti-mail-filled" />
            </div>
            <span className="sp-details-field-value">
              {hasMeaningfulValue(patient?.email) ? patient?.email : 'N/A'}
            </span>
          </div>
          <div className="sp-details-email-row">
            <div className="sp-details-phone-icon" aria-hidden>
              <i className="ti ti-map-pin-filled" />
            </div>
            <span className="sp-details-field-value">{addressLine}</span>
          </div>
        </div>
      </section>

      <section
        className={`sp-details-section${
          afterPreferences ? '' : ' sp-details-section--last'
        }`}
      >
        <SectionHeader iconClass="mgc_star_fill" title="Preferences" />
        <div className="sp-details-section-body sp-details-fields">
          <div className="sp-details-grid-row">
            <DetailField
              label="Preferred provider"
              value={preferredProviderDisplay}
            />
            <DetailField
              label="Preferred hygienist"
              value={preferredHygienistDisplay}
            />
          </div>
          <div className="sp-details-grid-row">
            <DetailField
              label="Preferred appt. time"
              value={patient?.preferred_appointment_time || 'N/A'}
            />
            <DetailField
              label="Contact preference"
              value={patient ? contactPreferenceLabels(patient) : 'N/A'}
            />
          </div>
        </div>
      </section>

      {afterPreferences}
    </div>
  )
}

export default Details
